import { supabase } from '../services/supabaseClient'

/**
 * Helper to call Supabase Edge Functions with proper authentication
 */
export async function callEdgeFunction<T = any>(
  functionName: string, 
  body?: any,
  options?: RequestInit
): Promise<T> {
  // Get the current session token
  const { data: { session } } = await supabase.auth.getSession()
  
  if (!session?.access_token) {
    throw new Error('No authentication token available')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured')
  }

  // Construct the Edge Function URL
  const url = `${supabaseUrl}/functions/v1/${functionName}`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...options,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ 
      error: response.statusText || 'Request failed' 
    }))
    throw new Error(error.message || error.error || `Edge function ${functionName} failed`)
  }

  return response.json()
}

/**
 * Map of Vercel API endpoints to Supabase Edge Functions
 */
export const EDGE_FUNCTION_MAPPING = {
  '/api/salesforce/refresh-token': 'salesforce-refresh-token',
  '/api/salesforce/refresh-org-auth': 'salesforce-refresh-org-auth',
  '/api/salesforce/flows/regenerate-summary': 'salesforce-regenerate-summary',
  '/api/salesforce/flows/check-updates': 'salesforce-check-updates',
  '/api/salesforce/flows/check-updates-enhanced': 'salesforce-check-updates-enhanced',
  '/api/salesforce/flows/comprehensive-sync': 'salesforce-comprehensive-sync',
  '/api/salesforce/trigger-analysis': 'salesforce-trigger-analysis',
  '/api/salesforce/performance-scorecard': 'salesforce-performance-scorecard',
  '/api/salesforce/sync-flows-for-do-worker': 'salesforce-sync-flows',
  '/api/salesforce/flows/refresh-all': 'salesforce-refresh-all',
  '/api/salesforce/sync-flows-batch': 'salesforce-sync-batch',
  '/api/salesforce/flow-interview-data': 'salesforce-interview-data',
  '/api/salesforce/calculate-flow-heat': 'salesforce-calculate-heat',
  '/api/salesforce/test-field-availability': 'salesforce-field-test',
  '/api/performance/score-all-flows': 'salesforce-score-all-flows',
  // Add more mappings as we create more Edge Functions
} as const

/**
 * Check if an endpoint has been migrated to Edge Functions
 */
export function isEdgeFunctionMigrated(endpoint: string): boolean {
  // Temporarily disable edge functions due to CORS issues
  return false
  // return endpoint in EDGE_FUNCTION_MAPPING
}

/**
 * Get the Edge Function name for a Vercel endpoint
 */
export function getEdgeFunctionName(endpoint: string): string | null {
  return EDGE_FUNCTION_MAPPING[endpoint as keyof typeof EDGE_FUNCTION_MAPPING] || null
}